import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Divider,
  Grid2,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  useTheme,
  IconButton,
  Icon,
  Collapse,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import {
  DeleteSuggestedProductParams,
  isStockCheckRequestLocked,
  MAX_EXECUTION_WAREHOUSES,
  normalizeWarehouseIdentifiers,
  Product,
  sameWarehouseIdentifiers,
  Stock,
  StockCheckExecutionWarehouse,
  StockCheckRequestStatus,
  StockCheckSuggestedProducts,
} from "../../../../../models/Stock/Stock";
import {
  formatDate,
  formatMinutes,
} from "../../../../../views/components/common/functions/formatDate";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import calender_icon from "../../../../../assets/calender_icon.png";
import SuggestedProductsModal from "./SuggestedProductsPopup";
import { observer } from "mobx-react";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { OrderType } from "../../../../../models/Order/Order";
import { useStores } from "../../../use-stores";

interface StockCheckRequestTableProps {
  lists: Stock[];
  padding?: number;
  boxShadow?: string;
  onEditClick: (requestId: number, prod: Product) => void;
  onStatusChange: (requestId: number, id: number) => void;
  saveStatus: (requestId: number) => Promise<void>;
  saveWarehouses: (
    requestId: number,
    warehouseIdentifiers: string[],
  ) => Promise<void>;
  onDeleteSuggestedProduct?: (
    params: DeleteSuggestedProductParams,
  ) => Promise<void>;
}

const status: FilterValue[] = [
  { id: "1", value: "Open" },
  { id: "2", value: "Closed" },
];

const StockCheckRequestTable: React.FC<StockCheckRequestTableProps> = ({
  lists,
  padding,
  boxShadow,
  onEditClick,
  onStatusChange,
  saveStatus,
  saveWarehouses,
  onDeleteSuggestedProduct,
}) => {
  const theme = useTheme();
  const { userStore, stockStore, warehouseStore, orderStore } = useStores();
  const readOnly =
    Number(
      orderStore.existingOrder?.type_id ?? orderStore.orderRequest?.type_id,
    ) === OrderType.POD;
  const [expandedList, setExpandedList] = useState<number | null>(null);
  const [warehouseDrafts, setWarehouseDrafts] = useState<
    Record<number, string[]>
  >({});
  const [warehouseErrors, setWarehouseErrors] = useState<
    Record<number, string>
  >({});
  const [warehouseSuccess, setWarehouseSuccess] = useState<
    Record<number, string>
  >({});
  const [warehouseListError, setWarehouseListError] = useState("");
  const [savingWarehouses, setSavingWarehouses] = useState<
    Record<number, boolean>
  >({});
  const [statusErrors, setStatusErrors] = useState<Record<number, string>>({});
  const [statusSuccess, setStatusSuccess] = useState<Record<number, string>>(
    {},
  );

  useEffect(() => {
    let active = true;
    warehouseStore.searchWarehouse().catch(() => {
      if (!active || warehouseStore.warehouses.length > 0) return;
      setWarehouseListError(
        "Could not load warehouses. Try again before closing this request.",
      );
    });
    return () => {
      active = false;
    };
  }, [warehouseStore]);
  const [descriptionModal, setDescriptionModal] = useState<{
    isOpen: boolean;
    description: string;
  }>({ isOpen: false, description: "" });

  const handleViewClick = (
    products: StockCheckSuggestedProducts[],
    requestId: number,
    product: Product,
    editable: boolean,
  ) => {
    if (products.length === 0) return;
    stockStore.openSuggestedProductsModal(
      products,
      requestId,
      product,
      editable,
    );
  };

  const handleModalClose = () => {
    stockStore.closeSuggestedProductsModal();
  };

  const handleDeleteSuggestedProduct = (
    requestId: number,
    productId: number,
    suggestedProductId: number,
  ) => {
    onDeleteSuggestedProduct?.({
      requestId,
      productId,
      suggestedProductId,
    })?.then(() => {
      handleModalClose();
    });
  };

  const toggleAccordion = (index: number) => {
    setExpandedList(expandedList === index ? null : index);
  };

  const readErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === "object" && error !== null) {
      const responseMessage = (
        error as { response?: { data?: { message?: string } } }
      ).response?.data?.message;
      if (responseMessage) return responseMessage;
      if (error instanceof Error && error.message) return error.message;
    }
    return fallback;
  };

  const selectedWarehouseIdentifiers = (list: Stock) => {
    if (!list.id)
      return normalizeWarehouseIdentifiers(list.warehouse_identifiers);
    return normalizeWarehouseIdentifiers(
      warehouseDrafts[list.id] ?? list.warehouse_identifiers,
    );
  };

  const warehouseOptions = (list: Stock): FilterValue[] => {
    const options = new Map<string, FilterValue>();
    (warehouseStore.warehouses ?? []).forEach((warehouse) => {
      if (warehouse.active === false || !warehouse.identifier) return;
      options.set(warehouse.identifier, {
        id: warehouse.identifier,
        value: warehouse.identifier,
        subtitle: warehouse.city || undefined,
      });
    });
    (list.warehouses ?? []).forEach((warehouse) => {
      if (!warehouse.identifier || options.has(warehouse.identifier)) return;
      options.set(warehouse.identifier, {
        id: warehouse.identifier,
        value: warehouse.identifier,
        subtitle: warehouse.city || undefined,
      });
    });
    return Array.from(options.values());
  };

  const assignedWarehouses = (list: Stock): StockCheckExecutionWarehouse[] => {
    if (list.warehouses && list.warehouses.length > 0) return list.warehouses;
    return (list.warehouse_identifiers ?? []).map((identifier) => ({
      warehouse_id: null,
      identifier,
      name: null,
      city: null,
    }));
  };

  const handleWarehouseSelect = (
    requestId: number,
    selected: FilterValue[],
  ) => {
    setWarehouseDrafts((current) => ({
      ...current,
      [requestId]: selected.map((item) => String(item.id)),
    }));
    setWarehouseErrors((current) => ({ ...current, [requestId]: "" }));
    setWarehouseSuccess((current) => ({ ...current, [requestId]: "" }));
  };

  const hasUnsavedWarehouses = (list: Stock) =>
    !sameWarehouseIdentifiers(
      selectedWarehouseIdentifiers(list),
      list.warehouse_identifiers,
    );

  const canSaveStatus = (list: Stock) =>
    normalizeWarehouseIdentifiers(list.warehouse_identifiers).length > 0 &&
    !hasUnsavedWarehouses(list);

  const handleSaveWarehouses = async (list: Stock) => {
    if (!list.id) return;
    const requestId = list.id;
    const identifiers = selectedWarehouseIdentifiers(list);

    setWarehouseSuccess((current) => ({ ...current, [requestId]: "" }));
    if (identifiers.length === 0) {
      setWarehouseErrors((current) => ({
        ...current,
        [requestId]: "Select at least one execution warehouse.",
      }));
      return;
    }
    if (identifiers.length > MAX_EXECUTION_WAREHOUSES) {
      setWarehouseErrors((current) => ({
        ...current,
        [requestId]: `Select at most ${MAX_EXECUTION_WAREHOUSES} execution warehouses.`,
      }));
      return;
    }

    setWarehouseErrors((current) => ({ ...current, [requestId]: "" }));
    setSavingWarehouses((current) => ({ ...current, [requestId]: true }));
    try {
      await saveWarehouses(requestId, identifiers);
      setWarehouseDrafts((current) => {
        const next = { ...current };
        delete next[requestId];
        return next;
      });
      setWarehouseSuccess((current) => ({
        ...current,
        [requestId]: "Execution warehouses saved.",
      }));
    } catch (error: unknown) {
      setWarehouseErrors((current) => ({
        ...current,
        [requestId]: readErrorMessage(
          error,
          "Failed to save execution warehouses.",
        ),
      }));
    } finally {
      setSavingWarehouses((current) => ({ ...current, [requestId]: false }));
    }
  };

  const handleSaveStatus = async (list: Stock) => {
    if (!list.id) return;
    const requestId = list.id;
    const isClosing = list.status === StockCheckRequestStatus.CLOSED;

    setStatusSuccess((current) => ({ ...current, [requestId]: "" }));
    if (!canSaveStatus(list)) {
      setStatusErrors((current) => ({
        ...current,
        [requestId]: "Save execution warehouses before updating the status.",
      }));
      return;
    }

    setStatusErrors((current) => ({ ...current, [requestId]: "" }));
    try {
      await saveStatus(requestId);
      setStatusSuccess((current) => ({
        ...current,
        [requestId]: isClosing
          ? "Stock check request closed."
          : "Status saved.",
      }));
    } catch (error: unknown) {
      setStatusErrors((current) => ({
        ...current,
        [requestId]: readErrorMessage(
          error,
          "Failed to update the stock check request.",
        ),
      }));
    }
  };

  return (
    <>
      <Box sx={{ borderRadius: 2 }}>
        <Card
          sx={{
            p: padding || { xs: 2, md: 3 },
            borderRadius: "12px",
            border: `1px solid ${theme.palette.customColors.lightGray}`,
            boxShadow:
              boxShadow ||
              `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <CustomTypography variant="heading4" fontWeight={700}>
              Stock Check Requests
            </CustomTypography>
          </Box>
          {lists &&
            lists.length > 0 &&
            lists[0].min_products_timeline &&
            lists[0].min_products_timeline > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  gap: 2,
                  paddingY: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CustomTypography
                    style={{
                      color: theme.palette.customColors.greyTextColor,
                    }}
                    variant="body2"
                    fontWeight={600}
                  >
                    Min Order Timeline:
                  </CustomTypography>
                  <CustomTypography
                    style={{
                      color: theme.palette.customColors.text,
                    }}
                    variant="heading6"
                    fontWeight={700}
                  >
                    {lists[0].min_products_timeline} days
                  </CustomTypography>
                </Box>
                {lists[0].min_suggested_products_timeline &&
                  lists[0].min_suggested_products_timeline > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                        variant="body2"
                        fontWeight={600}
                      >
                        Min Order Timeline (Suggested Products):
                      </CustomTypography>
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.text,
                        }}
                        variant="heading6"
                        fontWeight={700}
                      >
                        {lists[0].min_suggested_products_timeline} days
                      </CustomTypography>
                    </Box>
                  )}
              </Box>
            )}

          {lists.map((list, index) => (
            <Box key={list.id ?? index}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  paddingY: 1,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <CustomTypography
                      variant="heading6"
                      fontWeight={700}
                      style={{ marginBottom: 10 }}
                    >
                      {index + 1}.{" "}
                    </CustomTypography>
                    <CustomTypography
                      variant="heading4"
                      fontWeight={700}
                      style={{ marginBottom: 10 }}
                    >
                      #{list.id}
                    </CustomTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton sx={{ width: "12px", height: "12px" }}>
                      <Icon>
                        <img src={calender_icon} alt="calender-icon" />
                      </Icon>
                    </IconButton>
                    <CustomTypography
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                      }}
                      variant="body2"
                      fontWeight={600}
                    >
                      Created Date:
                    </CustomTypography>
                    <CustomTypography
                      style={{
                        color: theme.palette.customColors.text,
                      }}
                      variant="body1"
                      fontWeight={600}
                    >
                      {list.created_date ? formatDate(list.created_date) : ""}
                    </CustomTypography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                        variant="body2"
                        fontWeight={600}
                      >
                        Closed By:
                      </CustomTypography>
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.text,
                        }}
                        variant="body1"
                        fontWeight={600}
                      >
                        {list.closer_name || "-"}
                      </CustomTypography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                        variant="body2"
                        fontWeight={600}
                      >
                        TAT:
                      </CustomTypography>
                      <CustomTypography
                        style={{
                          color: theme.palette.customColors.text,
                        }}
                        variant="body1"
                        fontWeight={600}
                      >
                        {list.tat ? formatMinutes(list.tat) : "-"}
                      </CustomTypography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CustomTypography variant="body2" fontWeight={600}>
                    Status:
                  </CustomTypography>
                  <>
                    {(list.status === 1 || list.status === 2) && (
                      <>
                        <AppDropDown
                          title=""
                          isMultiSelect={false}
                          value={list.status?.toString() || ""}
                          list={status}
                          height="36px"
                          listHeight="30px"
                          fontSize="14px"
                          width="120px"
                          disabled={readOnly}
                          onSingleSelectionChange={(id) => {
                            if (readOnly) return;
                            onStatusChange(list.id!, Number(id));
                            if (!list.id) return;
                            setStatusErrors((current) => ({
                              ...current,
                              [list.id!]: "",
                            }));
                            setStatusSuccess((current) => ({
                              ...current,
                              [list.id!]: "",
                            }));
                          }}
                        />
                        <AppButton
                          variant="primary"
                          disabled={readOnly || !canSaveStatus(list)}
                          onClick={() => {
                            if (readOnly) return;
                            handleSaveStatus(list);
                          }}
                          label="Save"
                        />
                      </>
                    )}
                    {list.status !== 1 && list.status !== 2 && (
                      <CustomTypography variant="heading7" fontWeight={600}>
                        {list.status_label}
                      </CustomTypography>
                    )}
                  </>
                  <AppButton
                    variant="primary"
                    onClick={() => toggleAccordion(index)}
                    label={
                      expandedList === index ? "Hide Products" : "View Products"
                    }
                  />
                </Box>
              </Box>
              {list.id ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <InlineFormError message={statusErrors[list.id]} />
                  {statusSuccess[list.id] ? (
                    <CustomTypography
                      variant="heading8"
                      fontWeight={600}
                      style={{ color: theme.palette.customColors.successGreen }}
                    >
                      {statusSuccess[list.id]}
                    </CustomTypography>
                  ) : null}
                </Box>
              ) : null}
              <Divider
                sx={{
                  my: 1.5,
                  borderColor: theme.palette.customColors.lightGray,
                }}
              />
              <Box sx={{ mb: 1 }}>
                <CustomTypography
                  variant="heading7"
                  fontWeight={600}
                  style={{ marginBottom: 4 }}
                >
                  Execution warehouses
                </CustomTypography>
                {list.id && !isStockCheckRequestLocked(list) ? (
                  <>
                    <CustomTypography
                      variant="body3"
                      fontWeight={500}
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                        marginBottom: 8,
                      }}
                    >
                      Save at least one warehouse before updating the status.
                    </CustomTypography>
                    <Grid2 container spacing={1} alignItems="center">
                      <Grid2 size={{ xs: 12, sm: 8, md: 5 }}>
                        <AppSearchDropdownMulti
                          title="Select warehouses"
                          isMultiSelect={true}
                          local
                          list={warehouseOptions(list)}
                          value={selectedWarehouseIdentifiers(list).map(
                            (identifier) => ({
                              id: identifier,
                              value: identifier,
                            }),
                          )}
                          handleSelect={(selected) =>
                            handleWarehouseSelect(list.id!, selected)
                          }
                          disabled={
                            readOnly || savingWarehouses[list.id] === true
                          }
                          fontSize="14px"
                          minWidth="100%"
                          borderColor={theme.palette.customColors.lightGray}
                          backgroundColor={theme.palette.customColors.white}
                          accentColor={theme.palette.customColors.purple}
                          inputHeight="40px"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: "auto" }}>
                        <AppButton
                          variant="primary"
                          label={
                            savingWarehouses[list.id]
                              ? "Saving..."
                              : "Save warehouses"
                          }
                          disabled={
                            readOnly ||
                            savingWarehouses[list.id] === true ||
                            selectedWarehouseIdentifiers(list).length === 0 ||
                            !hasUnsavedWarehouses(list)
                          }
                          onClick={() => {
                            handleSaveWarehouses(list);
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      minHeight: "40px",
                      alignItems: "center",
                    }}
                  >
                    {assignedWarehouses(list).length === 0 ? (
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        style={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                      >
                        None assigned
                      </CustomTypography>
                    ) : (
                      assignedWarehouses(list).map((warehouse) => (
                        <Box
                          key={warehouse.identifier}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: "999px",
                            backgroundColor:
                              theme.palette.customColors.blueLightBackground,
                            border: `1px solid ${theme.palette.customColors.softBorder}`,
                          }}
                        >
                          <CustomTypography variant="heading8" fontWeight={600}>
                            {warehouse.identifier}
                          </CustomTypography>
                        </Box>
                      ))
                    )}
                  </Box>
                )}
                {list.id ? (
                  <>
                    <InlineFormError message={warehouseErrors[list.id]} />
                    {warehouseListError ? (
                      <InlineFormError message={warehouseListError} />
                    ) : null}
                    {warehouseSuccess[list.id] ? (
                      <CustomTypography
                        variant="heading8"
                        fontWeight={600}
                        style={{
                          color: theme.palette.customColors.successGreen,
                          marginTop: 8,
                        }}
                      >
                        {warehouseSuccess[list.id]}
                      </CustomTypography>
                    ) : null}
                  </>
                ) : null}
              </Box>
              {list?.status && list?.status === 3 ? (
                <Box>
                  <CustomTypography variant="heading8">
                    Rejection reason – {list.rejection_reason || ""}
                  </CustomTypography>
                </Box>
              ) : null}

              {list?.status && list?.status === 4 ? (
                <Box>
                  <CustomTypography variant="heading8">
                    The request has expired on {formatDate(list.expiry_date)}
                  </CustomTypography>
                </Box>
              ) : null}

              <Collapse in={expandedList === index}>
                <TableContainer sx={{ borderRadius: "4px", mt: 3 }}>
                  <Table sx={{ tableLayout: "auto", width: "100%" }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.customColors.grayBackGroundColor,
                          borderRadius: "4px",
                          "& th": { borderBottom: "none" },
                        }}
                      >
                        <AppTableHeaderCell
                          title="S.No"
                          sx={{
                            textAlign: "center",
                            minWidth: "20px",
                          }}
                        />
                        <AppTableHeaderCell
                          title="ID"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="PRODUCT"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="ACTION NOTES"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="REQUEST ID"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />

                        <AppTableHeaderCell
                          title="REQUIRED QTY"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="INSTOCK"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="HOLDING PERIOD"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="SWAGEAZY INVENTORY QTY"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="VENDOR NAME"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="NOTES"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="STOCK QTY"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />

                        <AppTableHeaderCell
                          title="RAW INWARD TIMELINE"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="PRINT INWARD TIMELINE"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="IS DELETED"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        <AppTableHeaderCell
                          title="MODIFIED DATE"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />
                        {userStore.hasPermission(
                          EmployeeRoles.CHANGE_COST_PRICE,
                        ) ? (
                          <AppTableHeaderCell
                            title="Cost Price"
                            sx={{
                              color:
                                theme.palette.customColors
                                  .tableGrayHeadingColor,
                              textAlign: "center",
                            }}
                          />
                        ) : null}

                        <AppTableHeaderCell
                          title="SUGGESTED PRODUCTS"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            textAlign: "center",
                          }}
                        />

                        <AppTableHeaderCell
                          title="ACTION"
                          sx={{
                            color:
                              theme.palette.customColors.tableGrayHeadingColor,
                            position: "sticky",
                            zIndex: 10,
                            minWidth: "80px",
                            right: 0,
                            background:
                              theme.palette.customColors.grayBackGroundColor,
                          }}
                          minWidth="80px"
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {list.products?.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={16}
                            sx={{
                              borderBottom: "none",
                              padding: 3,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "fit-content",
                                textAlign: "center",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                fontWeight={700}
                                color="rgba(218, 218, 218, 1)"
                              >
                                NOTHING FOUND!
                              </CustomTypography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        list.products?.map(
                          (prod: Product, prodIndex: number) => (
                            <TableRow key={`${prodIndex}`}>
                              <AppTableRowCell
                                sx={{
                                  fontWeight: 700,
                                  minWidth: "20px",
                                }}
                              >
                                {prodIndex + 1}
                              </AppTableRowCell>

                              <AppTableRowCell>
                                {prod.product_id || "--"}
                              </AppTableRowCell>

                              <AppTableRowCell
                                minWidth="80px"
                                sx={{
                                  padding: 1,
                                }}
                              >
                                <Box display="flex" alignItems="center">
                                  <Box
                                    sx={{
                                      background: "rgba(242, 242, 242, 1)",
                                      padding: 1,
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 2,
                                      minHeight: "55px",
                                      maxHeight: "55px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <img
                                      style={{
                                        maxHeight: "38px",
                                        minHeight: "38px",
                                        maxWidth: "38px",
                                        minWidth: "38px",
                                      }}
                                      src={prod.product_image_url}
                                      alt={prod.product_name}
                                    />
                                  </Box>
                                  <Box sx={{ textAlign: "left" }}>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={700}
                                      title={prod.product_name}
                                      sx={{
                                        cursor: "default",
                                      }}
                                    >
                                      {prod.product_name}
                                    </CustomTypography>
                                  </Box>
                                </Box>
                              </AppTableRowCell>
                              <AppTableRowCell
                                sx={{ textAlign: "center", fontSize: "12px" }}
                              >
                                {prod.sourcing_notes ? (
                                  <AppButton
                                    label="View Notes"
                                    variant="transparentPrimary"
                                    customStyles={{
                                      fontSize: "0.8rem",
                                      padding: "4px 8px",
                                      minWidth: "auto",
                                    }}
                                    onClick={() => {
                                      setDescriptionModal({
                                        isOpen: true,
                                        description: prod.sourcing_notes || "",
                                      });
                                    }}
                                  />
                                ) : (
                                  "--"
                                )}
                              </AppTableRowCell>

                              <AppTableRowCell minWidth="80px">
                                {prod.request_id || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.quantity || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.in_stock === 1
                                  ? "YES"
                                  : prod.in_stock === 0
                                    ? "NO"
                                    : "-"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.holding_period || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.stock_available_qty || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.vendor_name || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.notes || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.vendor_stock || "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.expected_raw_inward_timeline
                                  ? `${prod.expected_raw_inward_timeline} days`
                                  : "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.expected_print_inward_timeline
                                  ? `${prod.expected_print_inward_timeline} days`
                                  : "--"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.active === 1 ? "No" : "Yes"}
                              </AppTableRowCell>
                              <AppTableRowCell minWidth="80px">
                                {prod.modified_date
                                  ? formatDate(prod.modified_date)
                                  : "--"}
                              </AppTableRowCell>
                              {userStore.hasPermission(
                                EmployeeRoles.CHANGE_COST_PRICE,
                              ) ? (
                                <AppTableRowCell minWidth="80px">
                                  {prod.cost_price ? prod.cost_price : "--"}
                                </AppTableRowCell>
                              ) : null}
                              <AppTableRowCell
                                minWidth="80px"
                                sx={{ cursor: "pointer", color: "blue" }}
                                onClick={() =>
                                  handleViewClick(
                                    prod?.suggested_products!,
                                    list.id!,
                                    prod,
                                    list.status === 1,
                                  )
                                }
                              >
                                {prod?.suggested_products?.length &&
                                prod?.suggested_products?.length > 0
                                  ? "VIEW"
                                  : "--"}
                              </AppTableRowCell>

                              <AppTableRowCell
                                minWidth="80px"
                                sx={{
                                  padding: "6px",
                                  paddingTop: "1rem",
                                  paddingBottom: "1rem",
                                  textAlign: "center",
                                  fontSize: 12,
                                  width: "10%",
                                  position: "sticky",
                                  right: 0,
                                  zIndex: 8,
                                  background: theme.palette.customColors.white,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "3px",
                                  }}
                                >
                                  <IconButton
                                    disabled={readOnly && list.status === 1}
                                    sx={{
                                      width: "10px",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                      },
                                      "&:active": {
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                    onClick={() => {
                                      if (readOnly && list.status === 1) return;
                                      onEditClick(list.id!, prod);
                                    }}
                                  >
                                    {list.status === 1 ? (
                                      <FontAwesomeIcon
                                        icon={faPencil}
                                        fontSize={14}
                                      />
                                    ) : (
                                      <FontAwesomeIcon
                                        icon={faEye}
                                        fontSize={14}
                                      />
                                    )}
                                  </IconButton>
                                </Box>
                              </AppTableRowCell>
                            </TableRow>
                          ),
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
              <Modal
                open={descriptionModal.isOpen}
                onClose={() =>
                  setDescriptionModal({ isOpen: false, description: "" })
                }
                aria-labelledby="description-modal-title"
                aria-describedby="description-modal-description"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: 800,
                    maxHeight: "80vh",
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    overflow: "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <CustomTypography variant="heading4" fontWeight={600}>
                      Action Notes
                    </CustomTypography>
                    <IconButton
                      onClick={() =>
                        setDescriptionModal({ isOpen: false, description: "" })
                      }
                      sx={{
                        color: theme.palette.grey[500],
                        "&:hover": {
                          color: theme.palette.grey[700],
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey[50],
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      maxHeight: "60vh",
                      overflow: "auto",
                    }}
                  >
                    <CustomTypography variant="body1" fontWeight={600}>
                      {descriptionModal.description}
                    </CustomTypography>
                  </Box>
                </Box>
              </Modal>
            </Box>
          ))}
        </Card>
      </Box>
      <SuggestedProductsModal
        open={stockStore.suggestedProductsModalOpen}
        onClose={handleModalClose}
        products={stockStore.selectedSuggestedProducts}
        requestId={stockStore.suggestedProductsModalRequestId}
        productId={stockStore.suggestedProductsModalProduct.product_id || 0}
        isEditable={
          !readOnly && stockStore.suggestedProductsModalIsEditable
        }
        onDelete={handleDeleteSuggestedProduct}
        onAdd={() => {
          onEditClick(
            stockStore.suggestedProductsModalRequestId,
            stockStore.suggestedProductsModalProduct,
          );
          handleModalClose();
        }}
      />
    </>
  );
};

export default observer(StockCheckRequestTable);
